<template>
  <FreePageLayout>
    <template #header>
      <FreeHeader class="p-4" is-centered>
        <template #leftAction>
          <FreeBackButton @click="submit" />
        </template>
        <template #default>
          <h2
            class="text-text-neutral-primary text-center text-lg font-semibold"
          >
            {{ t('ancillaries.seatMap.pageTitle') }}
          </h2>
        </template>
        <template #rightAction>
          <FriendsLabel
            v-if="freedomFriendsSelfInfo.data.value?.active"
            class="relative"
            is-short
          />
        </template>
      </FreeHeader>
    </template>
    <template #default>
      <div class="relative flex grow flex-col justify-between">
        <div class="space-y-3">
          <SeatMapFlight
            v-for="(flight, i) in mappedFlights"
            :key="i"
            :is-multi-trip-search="isMultiTripSearch"
            :flight="flight"
            :flight-idx="i"
            :seat-map-queries="seatMap.getFlightQueries(flight, i)"
            :passengers-data="seatMap.passengersData"
            :seat-map-state-by-flight="seatMap.getFlightState(i)"
            :passengers-keys="seatMap.passengersKeys"
            @onCommitState="updateSeatMapState($event, i)"
          >
            <template v-if="mappedFlights.length > 1 && !isMultiTripSearch">
              <p class="text-text-neutral-tertiary text-xs">
                {{
                  i === 0
                    ? t('ancillaries.seatMap.departure')
                    : t('ancillaries.seatMap.arrival')
                }}
              </p>
            </template>
          </SeatMapFlight>
        </div>
        <SeatMapPriceBlock
          v-if="hasFlightsSelectedSeats"
          class="-mx-4 w-screen rounded-t-2xl p-4"
          :seat-map-state="seatMap.seatMapStateBySegment.value"
          qa-id="seatMap.save"
          :passengers-keys="seatMap.passengersKeys"
          @onContinue="submit"
        />
      </div>
    </template>
  </FreePageLayout>
</template>

<script setup lang="ts">
  import { computed, ref } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useRouter } from 'vue-router';
  import { isEqual } from 'lodash-es';
  import { useI18n } from 'vue-i18n';
  import { hashCodeLib } from 'shared';
  import { FreeBackButton, FreeHeader, FreePageLayout } from 'freedom-shared';

  import {
    SeatMapFlight,
    seatMapLib,
    SeatMapPriceBlock,
    type SelectedSeatsState,
  } from '@/widgets/seat-map';

  import { bookingStateModel } from '@/features/booking-state';
  import { FriendsLabel, useFreedomFriendsSelfInfo } from '@/features/friends';

  import { sentryModel } from '@/shared/model';

  import { useSeatMap } from './model';

  const props = defineProps<{ offerId: string }>();

  const router = useRouter();
  const { t } = useI18n();

  const seatMapAlert = ref();
  const bookingStore = bookingStateModel.useBookingStore();
  const freedomFriendsSelfInfo = useFreedomFriendsSelfInfo();

  const { mappedFlights, offer } = storeToRefs(bookingStore);

  const isMultiTripSearch = computed(() => {
    const query = offer.value?.$meta?.search_query;
    if (typeof query !== 'string') {
      return false;
    }
    // Defensive: if a stale Vite dep cache returns an older shared/hashCodeLib
    // bundle that doesn't yet expose isMultiTripSearch, treat as non-MT
    // instead of crashing the page on a TypeError.
    if (typeof hashCodeLib.isMultiTripSearch !== 'function') {
      return false;
    }
    return hashCodeLib.isMultiTripSearch(query);
  });

  const initialStateBySegment = bookingStore.state.seatMap?.flatMap(
    state => state,
  );
  const seatMap = useSeatMap({
    offerId: props.offerId,
    mappedFlights: mappedFlights.value,
    passengersData: bookingStore.state.passengers,
    initialState: initialStateBySegment,
  });

  const hasFlightsSelectedSeats = computed(() =>
    mappedFlights.value
      .flatMap(flight => flight.segments)
      .some((_, idx) =>
        seatMapLib.hasSegmentSeats({
          seatMapState: seatMap.seatMapStateBySegment.value[idx],
          passengersKeys: seatMap.passengersKeys,
        }),
      ),
  );

  function updateSeatMapState(
    seatMapStateByFlight: SelectedSeatsState,
    flightIdx: number,
  ) {
    if (
      !isEqual(
        seatMap.seatMapStateByFlight.value[flightIdx],
        seatMapStateByFlight,
      )
    ) {
      seatMap.updateSeatMapState(seatMapStateByFlight, flightIdx);
      seatMapAlert.value?.show(t('ancillaries.seatMap.seatsSuccessfullyAdded'));
    }
  }

  function submit() {
    bookingStore.update({
      seatMap: hasFlightsSelectedSeats.value
        ? seatMap.seatMapStateBySegment.value
        : null,
    });

    sentryModel.addBreadcrumb({
      category: sentryModel.breadcrumbCategories.BOOKING,
      message: 'seats updated',
      level: sentryModel.level.INFO,
    });

    router.back();
  }
</script>
